/**
 * Scroll Image Gallery Component
 * Changes displayed image based on user scroll position
 */

if (!customElements.get('scroll-image-gallery')) {
  class ScrollImageGallery extends HTMLElement {
    constructor() {
      super();
      this.images = [];
      this.currentIndex = 0;
      this.isVisible = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollHandler = null;
      this.sectionElement = null;
    }

    connectedCallback() {
      this.images = Array.from(this.querySelectorAll('.scroll-gallery__image'));
      this.sectionElement = this.closest('.featured-product-section') || this.closest('section');
      
      if (this.images.length === 0) return;

      // Set initial state - show first image
      this.showImage(0);
      
      this.setupIntersectionObserver();

      if (!this.reduceMotion) {
        this.setupScrollListener();
      }
    }

    disconnectedCallback() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
    }

    /**
     * Sets up IntersectionObserver to detect when component enters viewport
     */
    setupIntersectionObserver() {
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            requestAnimationFrame(() => this.updateImage());
          }
        });
      }, observerOptions);

      observer.observe(this.sectionElement || this);
    }

    /**
     * Sets up throttled scroll listener for image switching
     */
    setupScrollListener() {
      this.scrollHandler = this.throttle(() => {
        if (!this.isVisible) return;
        requestAnimationFrame(() => this.updateImage());
      }, 50); // Throttle to ~20fps for smoother transitions

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    /**
     * Calculates scroll progress through the section
     * @returns {number} Progress from 0 to 1
     */
    calculateScrollProgress() {
      const target = this.sectionElement || this;
      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementHeight = target.offsetHeight;

      // Element is below viewport
      if (rect.top > viewportHeight) return 0;

      // Element is above viewport
      if (rect.bottom < 0) return 1;

      // Calculate progress through the element
      const totalTravelDistance = viewportHeight + elementHeight;
      const distanceTraveled = viewportHeight - rect.top;

      const progress = distanceTraveled / totalTravelDistance;
      return Math.max(0, Math.min(1, progress));
    }

    /**
     * Updates displayed image based on scroll position
     */
    updateImage() {
      const scrollProgress = this.calculateScrollProgress();
      const imageCount = this.images.length;
      
      if (imageCount === 0) return;

      // Calculate which image should be shown based on scroll progress
      // Divide progress into equal segments for each image
      const segmentSize = 1 / imageCount;
      let newIndex = Math.floor(scrollProgress / segmentSize);
      
      // Clamp to valid range
      newIndex = Math.max(0, Math.min(imageCount - 1, newIndex));

      if (newIndex !== this.currentIndex) {
        this.showImage(newIndex);
      }
    }

    /**
     * Shows the image at the given index
     * @param {number} index - Index of image to show
     */
    showImage(index) {
      this.images.forEach((img, i) => {
        if (i === index) {
          img.classList.add('is-active');
          img.setAttribute('aria-hidden', 'false');
        } else {
          img.classList.remove('is-active');
          img.setAttribute('aria-hidden', 'true');
        }
      });
      
      this.currentIndex = index;
      
      // Update indicator dots if present
      const dots = this.querySelectorAll('.scroll-gallery__dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    /**
     * Throttle function to limit execution rate
     * @param {Function} func - Function to throttle
     * @param {number} limit - Minimum time between calls in ms
     * @returns {Function} Throttled function
     */
    throttle(func, limit) {
      let inThrottle;
      return function (...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    }
  }

  customElements.define('scroll-image-gallery', ScrollImageGallery);
}

// Re-initialize on Shopify theme editor events
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const galleryElement = event.target.querySelector('scroll-image-gallery');
    if (galleryElement) {
      // Reset and reinitialize
      const images = galleryElement.querySelectorAll('.scroll-gallery__image');
      if (images.length > 0) {
        images[0].classList.add('is-active');
      }
    }
  });
}

